// src/utils/natureValidation.ts

/**
 * Data nature types supported by the backend
 */
export type DataNature = 'price' | 'volume' | 'fundamental' | 'signal' | 'generic' | 'result';

/**
 * Operator types that require nature validation
 */
export type ArithmeticOperator = 'Add' | 'Subtract' | 'Multiply' | 'Divide';

/**
 * Validates if two data natures are compatible for a given operator
 * 
 * Rules:
 * - Add/Subtract: Requires same nature (or generic/signal wildcard)
 * - Multiply/Divide: Permissive, allows cross-nature operations
 * - Comparisons: Always allowed
 */
export function validateNatureCompatibility(
    leftNature: DataNature | undefined,
    rightNature: DataNature | undefined,
    operator: string
): { isValid: boolean; warning?: string } {
    // If we don't have nature info yet, assume valid
    if (!leftNature || !rightNature) {
        return { isValid: true };
    }

    // Only validate arithmetic operations
    if (!['Add', 'Subtract', 'Multiply', 'Divide'].includes(operator)) {
        return { isValid: true };
    }

    // Generic and signal are wildcards - always compatible
    if (leftNature === 'generic' || rightNature === 'generic') {
        return { isValid: true };
    }
    if (leftNature === 'signal' || rightNature === 'signal') {
        return { isValid: true };
    }

    // Same nature is always OK
    if (leftNature === rightNature) {
        return { isValid: true };
    }

    // For Add/Subtract, different natures are NOT OK
    if (operator === 'Add' || operator === 'Subtract') {
        return {
            isValid: false,
            warning: `Cannot ${operator.toLowerCase()} incompatible data natures: ${leftNature} ${operator === 'Add' ? '+' : '-'} ${rightNature}. Only combine data of the same nature (e.g., price + price, volume + volume).`
        };
    }

    // For Multiply/Divide, cross-nature is allowed (e.g., price / volume)
    return { isValid: true };
}

/**
 * Get the nature color for visual indicators
 */
export function getNatureColor(nature: DataNature | undefined): string {
    switch (nature) {
        case 'price': return 'var(--nature-price)';
        case 'volume': return 'var(--nature-volume)';
        case 'fundamental': return 'var(--nature-fundamental)';
        case 'signal': return 'var(--nature-signal)';
        case 'result': return 'var(--nature-result)';
        default: return 'var(--text-muted)';
    }
}

/**
 * Get a human-readable label for a nature
 */
export function getNatureLabel(nature: DataNature | undefined): string {
    if (!nature) return 'Unknown';
    return nature.charAt(0).toUpperCase() + nature.slice(1);
}
